import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react'
import { useOrbit } from './Common/Orbit/useOrbit'
import { withLcmChip } from './Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from './Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from './Common/Orbit/orbitSliceHelpers'
import { resolveOrbitActionId } from './Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../services/actions/registry'
import { buildBoardCanvasSlices, buildBoardCardSlices, buildCellSlices, buildColumnHeaderSlices } from './views/board.orbit'
import { PathOnlyRow } from './Common/PathOnlyRow'
import {
  collection,
  onSnapshot,
  serverTimestamp,
} from 'firebase/firestore'
import AccountTreeRoundedIcon from '@mui/icons-material/AccountTreeRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'

import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import ContentPasteRoundedIcon from '@mui/icons-material/ContentPasteRounded'
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded'
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded'
import FlagRoundedIcon from '@mui/icons-material/FlagRounded'
import FolderRoundedIcon from '@mui/icons-material/FolderRounded'
import InsertDriveFileRoundedIcon from '@mui/icons-material/InsertDriveFileRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import RemoveCircleOutlineRoundedIcon from '@mui/icons-material/RemoveCircleOutlineRounded'

import RestoreFromTrashRoundedIcon from '@mui/icons-material/RestoreFromTrashRounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded'
import SaveAsRoundedIcon from '@mui/icons-material/SaveAsRounded'
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded'
import { useAuth } from '../context/useAuth'
import { useDisplayName } from '../hooks/useDisplayName'
import { usePushBackHandler } from '../context/BackStackContext'
import { useIsMemberMobile } from '../member/useIsMemberMobile'
import { MemberNodeDetailsSheet } from './Member/MemberNodeDetailsSheet'
import { useTileTapGestures } from './useTileTapGestures'
import type { ButtonGroupItem } from './Common/ButtonGroup'
import { useManageDraft } from '../context/manageDraftContextCore'
import { usePessimisticLock } from '../hooks/usePessimisticLock'
import { useTimeline } from '../context/useTimeline'
import { useToast } from '../context/useToast'
import { useWorkspace } from '../context/useWorkspace'
import { AppDialog } from './Common/AppDialog'
import { GoalClientPicker } from './DrawingBoard/GoalClientPicker'
import { Breadcrumb, DatePicker, Select, StatusChip, TextInput, Textarea, type CrumbSpec, type DropdownItem } from './Common'
import { Button } from './Common/Button'
import { Pressable } from './Common/Pressable'
import { ConfirmDialog } from './DetailedView/ConfirmDialog'
import { WORKSPACE_DEFAULT_CHECKLIST_ID, type TimelineElement, type TimelineElementType } from '../context/TimelineContext'
import type { ManageItemKind } from '../types/manageDraftState'
import type { BoardActionOps } from '../services/actions/action'
import { firestoreDb } from '../services/firebase'
import { recordWorkspaceActivity } from '../services/activityService'
import { createChecklistNative, deleteChecklistNative, writeChecklistUpdate } from '../services/checklistWriter'
import {
  binElements,
  restoreElements,
  moveElements,
  reparentElements,
  deleteForeverElements,
  binChecklist,
  restoreChecklist,
  moveChecklist,
} from '../services/elementRestructureService'
import { emitExperienceEvent } from '../utils/experienceEvents'
import {
  TIMELINE_BOARD_KEYS,
  TIMELINE_BOARD_LABELS,
  type ChecklistItem,
  type ChecklistWorkflowStatus,
  type TimelineBoardKey,
  type UserRole,
} from '../types/schema'
import type { WorkspaceRole } from '../types/workspace'
import { parseWorkspaceRole, roleCanPerform } from '../utils/workspacePermissions'
import { ChecklistTypeChip } from './ChecklistBoard/ChecklistTypeChip'
import { isRowLockedByPeer } from './manageRowLock'
import { getWorkspaceLimitValue } from '../utils/workspaceLimits'
import { createStripboardRecordDefaults } from '../utils/stripboardDefaults'
import { usePersistedState } from '../hooks/usePersistedState'
import { isChecklistGate } from '../utils/checklistDependencyState'
import { getNlsString } from '../nls/nlsRuntime'
import { CAP_KEY_TO_FEATURE_KEY } from '../config/elementCounting'
import { WORKSPACE_COUNT_HARD_CAPS } from '../config/workspaceLimitCaps'

import { ItemAttachmentsPanel } from './Forms/ItemAttachmentsPanel'
import { NodeBudgetLimitCard } from './Budget/NodeBudgetLimitCard'
import { setManageActionsBar } from './Manage/manageActionsBarStore'
import type { ItemAttachmentTarget } from '../services/itemAttachmentService'

type ExplorerEntry =
  | { kind: 'element'; id: string; element: TimelineElement }
  | { kind: 'checklist'; id: string; item: ChecklistItem }

type RenderableEntry = ExplorerEntry

type ClipboardEntry = {
  op: 'copy' | 'cut'
  entry: ExplorerEntry
}

type MoveIntent = {
  entry: ExplorerEntry
  targetBoard: TimelineBoardKey
  sourceBoard?: TimelineBoardKey
}

import { isRepeatPageStateKey } from '../services/repeat'

const ROOT_PREFIX = 'board-root:'

// eslint-disable-next-line react-refresh/only-export-components
export async function saveBoardExplorerChecklistChanges(params: {
  item: ChecklistItem
  pendingChanges: Record<string, unknown>
  actor: { uid: string; displayName: string | null }
  writeUpdate?: typeof writeChecklistUpdate
}): Promise<void> {
  const { item, pendingChanges, actor, writeUpdate = writeChecklistUpdate } = params
  const isRepeating = item.checklistType === 'DAILY' || item.checklistType === 'WEEKLY'
  const changes = { ...pendingChanges }
  if (isRepeating) {
    const hasWorkflowStatus = 'workflowStatus' in changes
    const nextWorkflowStatus = changes.workflowStatus as ChecklistWorkflowStatus
    for (const key of Object.keys(changes)) {
      if (isRepeatPageStateKey(key)) {
        delete changes[key]
      }
    }
    if (hasWorkflowStatus) {
      const { updateChecklistWorkflowStatus } = await import('../services/checklistStatusWriter')
      const { applyStripboardDelta } = await import('../services/stripboardDeltaService')
      await updateChecklistWorkflowStatus({
        item,
        workflowStatus: nextWorkflowStatus,
        actor: { id: actor.uid || undefined, name: actor.displayName || undefined },
        runDelta: applyStripboardDelta,
      })
    }
  }
  if (Object.keys(changes).length > 0) {
    await writeUpdate(item.id, changes, actor)
  }
}

/** Coerce arbitrary date strings (ISO with time, US-style, etc.) into the
 * YYYY-MM-DD shape that <input type="date"> requires. Returns '' for null /
 * empty / unparseable input so the input shows its placeholder. */
const normalizeDateForInput = (raw?: string | null): string => {
  if (!raw) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw
  if (raw.includes('T')) {
    const head = raw.split('T')[0]
    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) return head
  }
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return ''
  const yyyy = date.getFullYear().toString().padStart(4, '0')
  const mm = (date.getMonth() + 1).toString().padStart(2, '0')
  const dd = date.getDate().toString().padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

const boardRootId = (workspaceId: string, board: TimelineBoardKey) => `${ROOT_PREFIX}${workspaceId}:${board}`

const checklistRootId = (workspaceId: string, board: TimelineBoardKey) =>
  board === 'DASHBOARD' ? WORKSPACE_DEFAULT_CHECKLIST_ID : boardRootId(workspaceId, board)

const normalizeRole = (role: unknown): UserRole =>
  parseWorkspaceRole(role) ?? 'VIEWER'

const canWriteWithRole = (role: UserRole) => roleCanPerform(role as WorkspaceRole, 'EDITOR')

export const elementIcon = (type: TimelineElementType) => {
  if (type === 'GOAL') return <FlagRoundedIcon fontSize="inherit" />
  if (type === 'TASK') return <AssignmentTurnedInRoundedIcon fontSize="inherit" />
  return <AccountTreeRoundedIcon fontSize="inherit" />
}

// Pure helper — lives in boardExplorerUtils.ts so it can be unit-tested without
// dragging in Firebase / storageHubService.
export { computeIsDrilledIn } from './boardExplorerUtils'

export function computeOpenChain(
  openElement: TimelineElement | null,
  elementById: Map<string, TimelineElement>,
): TimelineElement[] {
  if (!openElement) return []
  const chain: TimelineElement[] = []
  let current: TimelineElement | null = openElement
  const seen = new Set<string>()
  while (current && !seen.has(current.id)) {
    chain.unshift(current)
    seen.add(current.id)
    current = current.parentId ? elementById.get(current.parentId) ?? null : null
  }
  return chain
}

export function getChildElementsByParent(
  parentId: string | null,
  board: TimelineBoardKey,
  elements: TimelineElement[],
): TimelineElement[] {
  if (parentId === null) {
    if (board === 'BIN') {
      const byId = new Map(elements.map((e) => [e.id, e]))
      return elements
        .filter((candidate) => {
          if ((candidate.board ?? 'DASHBOARD') !== 'BIN') return false
          if (!candidate.parentId) return true
          const parent = byId.get(candidate.parentId)
          return (parent?.board ?? 'DASHBOARD') !== 'BIN'
        })
        .sort((first, second) => first.type.localeCompare(second.type) || first.title.localeCompare(second.title))
    }
    return elements
      .filter((candidate) => (candidate.board ?? 'DASHBOARD') === board && (candidate.parentId ?? null) === null)
      .sort((first, second) => first.type.localeCompare(second.type) || first.title.localeCompare(second.title))
  }
  return elements
    .filter((candidate) => {
      if ((candidate.parentId ?? null) !== parentId) return false
      const isBin = (candidate.board ?? 'DASHBOARD') === 'BIN'
      return board === 'BIN' ? isBin : !isBin
    })
    .sort((first, second) => first.type.localeCompare(second.type) || first.title.localeCompare(second.title))
}


export type RenderBoardBreadcrumbParams = {
  board: TimelineBoardKey
  openChain: TimelineElement[]
  onBoardChange: (board: TimelineBoardKey) => void
  setOpenElementId: (id: string | null) => void
  getChildElements: (parentId: string | null) => TimelineElement[]
  onSelectElement?: (element: TimelineElement) => void
}

export function renderBoardBreadcrumb({
  board,
  openChain,
  onBoardChange,
  setOpenElementId,
  getChildElements,
  onSelectElement,
}: RenderBoardBreadcrumbParams): ReactNode {
  const drillCrumb = (
    id: string,
    label: string,
    onNavigate: () => void,
    childOptions: TimelineElement[],
  ): CrumbSpec => ({
    key: id,
    label,
    onLabelClick: onNavigate,
    ariaLabel: 'children',
    children: childOptions.map<DropdownItem>((option) => ({
      key: option.id,
      label: option.title || 'Untitled',
      icon: elementIcon(option.type),
      onSelect: () => {
        setOpenElementId(option.id)
        if (onSelectElement) onSelectElement(option)
      },
    })),
  })

  return (
    <Breadcrumb
      className="boardBreadcrumb"
      ariaLabel="Board breadcrumb"
      crumbs={[
        {
          key: 'board-switcher',
          label: null,
          ariaLabel: 'switch board',
          tourId: 'board-switcher',
          children: TIMELINE_BOARD_KEYS.map<DropdownItem>((key) => ({
            key,
            label: TIMELINE_BOARD_LABELS[key],
            onSelect: () => onBoardChange(key),
            isActive: key === board,
            ...(key === 'BIN' ? { tourId: 'board-switcher-bin' } : {}),
          })),
        },
        drillCrumb(
          'board',
          TIMELINE_BOARD_LABELS[board],
          () => setOpenElementId(null),
          getChildElements(null),
        ),
        ...openChain.map((chainItem) => drillCrumb(
          `element:${chainItem.id}`,
          chainItem.title || 'Untitled',
          () => setOpenElementId(chainItem.id),
          getChildElements(chainItem.id),
        )),
      ]}
    />
  )
}

export const BoardBreadcrumb = (props: RenderBoardBreadcrumbParams) => renderBoardBreadcrumb(props)

const nextElementType = (parent: TimelineElement | null): TimelineElementType => {
  if (!parent) return 'GOAL'
  if (parent.type === 'GOAL') return 'TASK'
  if (parent.type === 'TASK') return 'SUBTASK'
  return 'SUBTASK'
}

const newElementTitle = (type: TimelineElementType) =>
  type === 'GOAL' ? 'New goal' : type === 'TASK' ? 'New task' : 'New subtask'

const manageItemKindFor = (type: TimelineElementType): ManageItemKind => {
  if (type === 'GOAL') return 'goal'
  if (type === 'TASK') return 'task'
  return 'subtask'
}

const isElementEntry = (entry: ExplorerEntry | null): entry is Extract<ExplorerEntry, { kind: 'element' }> =>
  entry?.kind === 'element'

const isChecklistEntry = (entry: ExplorerEntry | null): entry is Extract<ExplorerEntry, { kind: 'checklist' }> =>
  entry?.kind === 'checklist'

const cloneElementPayload = (
  element: TimelineElement,
  overrides: Partial<TimelineElement> & { workspaceId: string },
) => ({
  type: element.type,
  title: `${element.title} copy`,
  description: element.description ?? '',
  weight: element.weight ?? 1,
  progress: element.progress ?? 0,
  parentId: overrides.parentId ?? element.parentId ?? null,
  workspaceId: overrides.workspaceId,
  board: overrides.board ?? element.board ?? 'DASHBOARD',
  checklist: element.checklist ?? [],
  readAccess: element.readAccess ?? [],
  writeAccess: element.writeAccess ?? [],
  directReadAccess: element.directReadAccess ?? [],
  directWriteAccess: element.directWriteAccess ?? [],
  startDate: element.startDate,
  endDate: element.endDate,
  color: element.color,
  createdAt: serverTimestamp(),
  lastUpdatedAt: serverTimestamp(),
})

type BoardExplorerProps = {
  // The explorer filters generically on `element.board`, so DASHBOARD is a
  // valid target too — the desktop "Boards" side-nav view and the member
  // app's Board → Folder sub-tab both render the Dashboard tree here.
  board: TimelineBoardKey
  onBoardChange: (board: TimelineBoardKey) => void
  onDashboardMoveComplete?: (movedId?: string) => void
  onOpenChecklistItem?: (item: ChecklistItem) => void
  headerControls?: ReactNode
  openElementId?: string | null
  onOpenElementIdChange?: (id: string | null) => void
  // Set by App.tsx after Move-to-Manage handoff. When the id becomes
  // available in the loaded elements (Firestore snapshot may lag by a tick),
  // the explorer opens and selects it. Consumed via onPendingOpenConsumed so
  // the caller can drop it and avoid re-triggering on later renders.
  pendingOpenElementId?: string | null
  onPendingOpenConsumed?: () => void
  /** Mirror selection from Review without drilling in. Only selects the element. */
  pendingSelectOnlyElementId?: string | null
  onPendingSelectOnlyConsumed?: () => void
  /** Notify parent of active element id and whether it is drilled in. */
  onActiveChange?: (elementId: string | null, isDrilledIn: boolean) => void
  /**
   * The current Manage mode. Board only publishes to the shared actions bar
   * store when this is 'board'. When 'review' (or undefined for non-Manage
   * contexts) the Board publishes null so Review's own publish wins.
   */
  manageMode?: 'board' | 'review'
}

const serializeDragData = (entry: ExplorerEntry) => JSON.stringify({ kind: entry.kind, id: entry.id })

interface ExplorerTileItemProps {
  entry: RenderableEntry
  isFolder: boolean
  title: string
  stateClass: string
  rowLockedByPeer: boolean
  lockTitle: string | null
  isSelected: boolean
  canEditBoard: boolean
  isMobile: boolean
  handleDropOnElement: (event: DragEvent, target: TimelineElement | null) => void
  onOpenChecklistItem?: (item: ChecklistItem) => void
  setOpenElementId: (id: string | null) => void
  setSelectedEntry: (entry: RenderableEntry | null) => void
  requireConfirmIfPending: (message: string, action: () => void) => void
}

function ExplorerTileItem({
  entry,
  isFolder,
  title,
  stateClass,
  rowLockedByPeer,
  lockTitle,
  isSelected,
  canEditBoard,
  isMobile,
  handleDropOnElement,
  onOpenChecklistItem,
  setOpenElementId,
  setSelectedEntry,
  requireConfirmIfPending,
}: ExplorerTileItemProps) {
  const isChecklist = entry.kind === 'checklist'
  const gestures = useTileTapGestures({
    isMobile,
    // Checklist tiles have their own full-page detail; the details sheet is
    // redundant for them. Gate long-press and double-tap the same way the
    // back tile does: disabled:true propagates into useLongPress so no
    // long-press fires at all, and disabledDoubleTap triggers the early-return
    // path in useTileTapGestures for double-tap.
    disabledLongPress: isChecklist,
    disabledDoubleTap: isChecklist,
    onSingleTap: () => {
      if (entry.kind === 'checklist') {
        requireConfirmIfPending(
          'You have unsaved changes. Discard them and switch?',
          () => {
            if (onOpenChecklistItem) {
              onOpenChecklistItem(entry.item)
              return
            }
            setSelectedEntry(entry)
          },
        )
      } else {
        requireConfirmIfPending(
          'You have unsaved changes. Discard them and open this folder?',
          () => {
            setOpenElementId(entry.element.id)
          },
        )
      }
    },
    onDoubleTap: () => {
      // Only reachable for element tiles (disabledDoubleTap gates checklist tiles).
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and switch?',
        () => {
          setSelectedEntry(entry)
        },
      )
    },
    onLongPress: () => {
      // Only reachable for element tiles (disabledLongPress gates checklist tiles).
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and switch?',
        () => {
          setSelectedEntry(entry)
        },
      )
    },
    onDesktopClick: () => {
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and switch?',
        () => {
          if (entry.kind === 'checklist' && onOpenChecklistItem) {
            onOpenChecklistItem(entry.item)
            return
          }
          setSelectedEntry(entry)
        },
      )
    },
    onDesktopDoubleClick: () => {
      if (entry.kind !== 'element') return
      const target = entry
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and open this folder?',
        () => {
          setOpenElementId(target.element.id)
          setSelectedEntry(target)
        },
      )
    },
  })

  return (
    <Pressable
      key={`${entry.kind}:${entry.id}`}
      data-orbit-target="card"
      data-card-id={entry.id}
      data-orbit-title={title}
      title={lockTitle ?? undefined}
      aria-disabled={rowLockedByPeer || undefined}
      className={`explorerTile${isSelected ? ' isSelected' : ''}${stateClass}${rowLockedByPeer ? ' rowLockedByPeer' : ''}`}
      draggable={canEditBoard}
      onDragStart={(event) => {
        event.dataTransfer.setData('application/x-timelining-entry', serializeDragData(entry))
        event.dataTransfer.effectAllowed = 'move'
      }}
      onDragOver={(event) => {
        if (isFolder && canEditBoard) event.preventDefault()
      }}
      onDrop={(event) => (entry.kind === 'element') ? void handleDropOnElement(event, entry.element) : undefined}
      {...gestures}
    >
      <span className={isFolder ? 'explorerFolderIcon' : 'explorerFileIcon'}>
        {isFolder
          ? <FolderRoundedIcon fontSize="inherit" />
          : <InsertDriveFileRoundedIcon fontSize="inherit" />}
        <StatusChip size="sm" tone="neutral" className="explorerTypeBadge">
          {entry.kind === 'element'
            ? elementIcon(entry.element.type)
            : <SubdirectoryArrowRightRoundedIcon fontSize="inherit" />}
        </StatusChip>
      </span>
      <span className="explorerTileName">{title || 'Untitled'}</span>
      {rowLockedByPeer && (
        <span className="rowLockedByPeerBadge" title={lockTitle ?? undefined}>
          <LockRoundedIcon fontSize="inherit" />
        </span>
      )}
      {entry.kind === 'checklist' && (
        <ChecklistTypeChip type={entry.item.checklistType ?? 'INDIVIDUAL'} />
      )}
    </Pressable>
  )
}

interface ExplorerBackTileItemProps {
  isMobile: boolean
  openElement: TimelineElement
  setOpenElementId: (id: string | null) => void
  setSelectedEntry: (entry: RenderableEntry | null) => void
  requireConfirmIfPending: (message: string, action: () => void) => void
}

function ExplorerBackTileItem({
  isMobile,
  openElement,
  setOpenElementId,
  setSelectedEntry,
  requireConfirmIfPending,
}: ExplorerBackTileItemProps) {
  const gestures = useTileTapGestures({
    isMobile,
    disabledLongPress: true,
    disabledDoubleTap: true,
    onSingleTap: () => {
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and go up?',
        () => setOpenElementId(openElement.parentId ?? null),
      )
    },
    onDoubleTap: () => {
      // Actively suppressed on back tile
      return
    },
    onLongPress: () => {
      // Actively suppressed on back tile
      return
    },
    onDesktopClick: () => {
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and clear selection?',
        () => setSelectedEntry(null),
      )
    },
    onDesktopDoubleClick: () => {
      requireConfirmIfPending(
        'You have unsaved changes. Discard them and go up?',
        () => setOpenElementId(openElement.parentId ?? null),
      )
    },
  })

  return (
    <Pressable
      className="explorerTile explorerBackTile"
      {...gestures}
    >
      <span className="explorerFolderIcon"><FolderRoundedIcon fontSize="inherit" /></span>
      <span className="explorerTileName">..</span>
    </Pressable>
  )
}

export const BoardExplorer = ({
  board,
  // onBoardChange: destructured from props type but never used inside this component
  // — caller passes it (MemberBoardHub) but it is not wired to any action here.
  // [CODE-REVIEW CR-4]: should this be forwarded to renderBoardBreadcrumb or removed from the type?
  onDashboardMoveComplete,
  onOpenChecklistItem,
  headerControls,
  openElementId: propsOpenElementId,
  onOpenElementIdChange,
  pendingOpenElementId,
  onPendingOpenConsumed,
  pendingSelectOnlyElementId,
  onPendingSelectOnlyConsumed,
  onActiveChange,
  manageMode,
}: BoardExplorerProps) => {
  const isMobile = useIsMemberMobile()
  const { currentUser, userProfile } = useAuth()
  const currentDisplayName = useDisplayName(userProfile ?? currentUser)
  const { activeWorkspaceId, activeRole, workspaces, canPerform } = useWorkspace()
  const { elements, createElements, updateElement, memberProfile, checklistItems: timelineChecklistItems, canPerformOnNode, pathOnlyElementIds = new Set() } = useTimeline()
  const [boardCounters, setBoardCounters] = useState<Record<string, { goals: number; tasks: number; subtasks: number; checklists: number }>>({})
  const [parentCounters, setParentCounters] = useState<Record<string, { tasks: number; subtasks: number; checklists: number }>>({})

  useEffect(() => {
    if (!activeWorkspaceId) return
    const unsubBoard = onSnapshot(
      collection(firestoreDb, 'workspaces', activeWorkspaceId, 'elementCounters'),
      (snap) => {
        const next: Record<string, { goals: number; tasks: number; subtasks: number; checklists: number }> = {}
        snap.forEach((docSnap) => {
          const d = docSnap.data()
          next[docSnap.id] = {
            goals: typeof d.goals === 'number' ? d.goals : 0,
            tasks: typeof d.tasks === 'number' ? d.tasks : 0,
            subtasks: typeof d.subtasks === 'number' ? d.subtasks : 0,
            checklists: typeof d.checklists === 'number' ? d.checklists : 0,
          }
        })
        setBoardCounters(next)
      },
      () => {},
    )
    const unsubParent = onSnapshot(
      collection(firestoreDb, 'workspaces', activeWorkspaceId, 'elementParentCounters'),
      (snap) => {
        const next: Record<string, { tasks: number; subtasks: number; checklists: number }> = {}
        snap.forEach((docSnap) => {
          const d = docSnap.data()
          next[docSnap.id] = {
            tasks: typeof d.tasks === 'number' ? d.tasks : 0,
            subtasks: typeof d.subtasks === 'number' ? d.subtasks : 0,
            checklists: typeof d.checklists === 'number' ? d.checklists : 0,
          }
        })
        setParentCounters(next)
      },
      () => {},
    )
    return () => {
      unsubBoard()
      unsubParent()
    }
  }, [activeWorkspaceId])
  const { showToast } = useToast()
  const lcmDispatchers = useLcmChipDispatchers()
  const boardOpsRef = useRef<BoardActionOps>({})
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) {
      showToast('Coming in Orbit Phase 6')
      return
    }
    const actionId = resolveOrbitActionId(sliceId, 'board')
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: currentUser?.uid ?? '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        boardOps: boardOpsRef.current,
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) {
      lcmDispatch(id, title)
      return
    }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [activeWorkspaceId, canPerform, currentUser?.uid, lcmDispatchers, showToast])
  const orbitDescriptor = useMemo(() => ({
    page: 'board',
    centerLabel: 'Board',
    onCreate: () => boardOpsRef.current?.createCard?.(),
    contextSlices: () => [],
    targets: withLcmChip({
      card: (node: HTMLElement) => buildBoardCardSlices(node, onOrbitAction),
      'column-header': (node: HTMLElement) => buildColumnHeaderSlices(node, onOrbitAction),
      cell: (node: HTMLElement) => buildCellSlices(node, onOrbitAction),
      canvas: () => buildBoardCanvasSlices(onOrbitAction),
    }, onOrbitAction),
  }), [onOrbitAction])
  useOrbit(orbitDescriptor)
  const manageDraft = useManageDraft()
  // Extends the existing checklist-side pessimistic lock system to timeline
  // goals / tasks / subtasks. Any pending change in Manage acquires a 5-min
  // edit lease keyed by the element id (with cascading child leases); other
  // users see the lock via `getExternalLock` and their Save / Discard are
  // gated by `isLockedByExternalUser` below.
  const { acquireEditLease, releaseLease, getExternalLock } = usePessimisticLock()
  const heldLeasesRef = useRef<Set<string>>(new Set())
  const [internalOpenElementId, setInternalOpenElementId] = usePersistedState<string | null>('boardExplorer.openElementId', null)
  const openElementId = propsOpenElementId !== undefined ? propsOpenElementId : internalOpenElementId
  const setOpenElementId = useCallback((id: string | null) => {
    if (onOpenElementIdChange) {
      onOpenElementIdChange(id)
    }
    setInternalOpenElementId(id)
  }, [onOpenElementIdChange, setInternalOpenElementId])
  const [selectedEntry, setSelectedEntry] = usePersistedState<ExplorerEntry | null>('boardExplorer.selectedEntry', null)
  const [clipboard, setClipboard] = useState<ClipboardEntry | null>(null)
  const [moveIntent, setMoveIntent] = useState<MoveIntent | null>(null)
  // GitHub-compare-style toggle for the details aside. 2-col = [tree | new],
  // 3-col = [tree | old | new]. Defaults follow per-item state but the toggle
  // is sticky once the user flips it explicitly.
  const [columnMode, setColumnMode] = usePersistedState<'2col' | '3col'>('boardExplorer.columnMode', '2col')
  const [columnModeOverridden] = useState(false)
  // Buffered edits for the right panel. Inputs write here, Save commits to
  // Firestore, Undo throws them away. The keys are field names on the
  // underlying element or checklist record.
  const [pendingChanges, setPendingChanges] = usePersistedState<Record<string, unknown>>('boardExplorer.pendingChanges', {})
  // When the user tries to switch / delete / move with unsaved edits we stash
  // the requested action here and surface a ConfirmDialog. Approving clears
  // pendingChanges and runs the action; cancelling does nothing.
  const [pendingConfirm, setPendingConfirm] = useState<{ message: string; action: () => void } | null>(null)
  const hasPendingChanges = Object.keys(pendingChanges).length > 0

  const boardRole = normalizeRole(memberProfile?.boardRoles?.[board] ?? memberProfile?.role ?? activeRole)
  const activeWorkspace = useMemo(
    () => workspaces.find((workspace) => workspace.id === activeWorkspaceId) ?? null,
    [activeWorkspaceId, workspaces],
  )
  const isFrozen = board === 'ARCHIVE' || board === 'BIN'
  const canEditBoard = canWriteWithRole(boardRole) && canPerform('board.edit') && !isFrozen
  const boardElements = useMemo(
    () => elements.filter((element) => (element.board ?? 'DASHBOARD') === board),
    [board, elements],
  )
  const checklistItems = useMemo(
    () => timelineChecklistItems.filter((item) => !activeWorkspaceId || item.workspaceId === activeWorkspaceId),
    [activeWorkspaceId, timelineChecklistItems],
  )
  // Built from the full elements set so that navigating into a folder whose
  // descendants have a drifted `board` field still resolves cleanly. Root-level
  // listing still uses `boardElements` to enforce per-board separation.
  const elementById = useMemo(() => new Map(elements.map((element) => [element.id, element])), [elements])
  const openElement = openElementId ? elementById.get(openElementId) ?? null : null
  const rootId = activeWorkspaceId ? checklistRootId(activeWorkspaceId, board) : ''

  // Clear buffered edits whenever the user switches to a different entry
  // (we already gate user-initiated switches via requireConfirmIfPending).
  // Exception: if manageDraft holds AI-proposed fields for the newly-selected
  // entry (seeded via seedProposedEdits from the Move-to-Manage handoff),
  // hydrate pendingChanges from them so the diff and inputs render Line's
  // proposal immediately. Save-all on the board then commits the proposal
  // via the standard savePendingChanges path.
  useEffect(() => {
    if (!selectedEntry?.id) {
      setPendingChanges({})
      return
    }
    const record = manageDraft.getRecord(selectedEntry.id)
    if (record?.proposedFields && Object.keys(record.proposedFields).length > 0) {
      setPendingChanges({ ...record.proposedFields })
    } else {
      setPendingChanges({})
    }
    // manageDraft intentionally omitted from deps — proposals shouldn't
    // re-hydrate on every ManageDraft state change, only on selection change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntry?.id])

  // Auto-switch the compare-column toggle based on the selected item's state:
  // modified → 3col (show old vs new), draft → 2col (nothing to compare).
  // Skipped after the user toggles manually so their choice sticks.
  useEffect(() => {
    if (columnModeOverridden) return
    if (!selectedEntry) return
    const state = manageDraft.getState(selectedEntry.id)
    if (state === 'modified') setColumnMode('3col')
    else if (state === 'draft') setColumnMode('2col')
  }, [selectedEntry?.id, manageDraft, columnModeOverridden, setColumnMode])

  // Gate a side-effecting action behind the pending-edits ConfirmDialog. If
  // there's nothing buffered, runs immediately. Otherwise the user is asked
  // to discard their edits; if they cancel, the action is dropped.
  const requireConfirmIfPending = useCallback((message: string, action: () => void) => {
    if (!hasPendingChanges) {
      action()
      return
    }
    setPendingConfirm({ message, action })
  }, [hasPendingChanges])

  // Mobile-only Android back handler:
  // 1. Details sheet handles its own back dismiss via MemberBottomSheet (usePushBackHandler).
  // 2. Drilled in (openElementId !== null) -> pop one level up.
  // 3. At root -> register nothing, so the shell exits the tab.
  const isBackEnabled = isMobile && Boolean(openElementId)
  usePushBackHandler(
    () => {
      if (openElementId) {
        requireConfirmIfPending(
          'You have unsaved changes. Discard them and go up?',
          () => setOpenElementId(openElement?.parentId ?? null),
        )
      }
    },
    [openElementId, openElement?.parentId, requireConfirmIfPending, setOpenElementId],
    isBackEnabled,
  )

  // Auto-open + select an element the parent (App.tsx) asked us to reveal on
  // Move-to-Manage handoff. Firestore snapshot may lag the state update, so
  // this waits until the id appears in elementById before acting. Once the
  // parent's request is honored, notify so the parent can drop the pending id
  // and this effect no longer re-fires on subsequent renders.
  useEffect(() => {
    if (!pendingOpenElementId) return
    const target = elementById.get(pendingOpenElementId)
    if (!target) return
    setOpenElementId(pendingOpenElementId)
    setSelectedEntry({ kind: 'element', id: pendingOpenElementId, element: target })
    onPendingOpenConsumed?.()
  }, [pendingOpenElementId, elementById, onPendingOpenConsumed, setOpenElementId, setSelectedEntry])

  useEffect(() => {
    if (!pendingSelectOnlyElementId) return
    const target = elementById.get(pendingSelectOnlyElementId)
    if (!target) return
    setSelectedEntry({ kind: 'element', id: pendingSelectOnlyElementId, element: target })
    onPendingSelectOnlyConsumed?.()
  }, [pendingSelectOnlyElementId, elementById, onPendingSelectOnlyConsumed, setSelectedEntry])

  useEffect(() => {
    if (!onActiveChange) return
    const activeId = selectedEntry?.kind === 'element' ? selectedEntry.id : (openElementId ?? null)
    const activeElement = selectedEntry?.kind === 'element'
      ? elementById.get(selectedEntry.id) ?? null
      : null
    const isDrilledIn = openElementId != null && (
      selectedEntry?.id === openElementId ||
      activeElement?.parentId === openElementId
    )
    onActiveChange(activeId, isDrilledIn)
  }, [selectedEntry?.id, selectedEntry?.kind, openElementId, elementById, onActiveChange])

  // Collapse the explorer to the new board's root on board switch.
  // elementById is keyed across all boards, so the cross-board-existence
  // cleanup below won't fire — the persisted cursor would otherwise keep
  // the previous board's goal/task/subtask crumbs visible inside the new
  // board, producing an empty folder view.
  useEffect(() => {
    setOpenElementId(null)
    setSelectedEntry(null)
  }, [board, setOpenElementId, setSelectedEntry])

  useEffect(() => {
    if (
      !(openElementId && !elementById.has(openElementId)) &&
      !(selectedEntry?.kind === 'element' && !elementById.has(selectedEntry.id))
    ) {
      return
    }
    const timeoutId = window.setTimeout(() => {
      if (openElementId && !elementById.has(openElementId)) setOpenElementId(null)
      if (selectedEntry?.kind === 'element' && !elementById.has(selectedEntry.id)) setSelectedEntry(null)
    }, 0)
    return () => window.clearTimeout(timeoutId)
  }, [elementById, openElementId, selectedEntry])

  const children = useMemo(() => {
    const parentId = openElement?.id ?? null
    const containerId = openElement?.id ?? rootId
    // At board root, restrict to elements stamped with this board. Inside an
    // opened folder, trust the parent chain — descendants whose own `board`
    // field drifted out of sync (legacy moves, AI generation) should still
    // surface under their real parent rather than vanishing.
    //
    // Bin nesting (fix 1.c.iii.fix1 / F-7):
    // 1. Inside an open folder: if board !== 'BIN', exclude children/files on 'BIN'.
    //    If board === 'BIN', only include children/files on 'BIN'.
    // 2. At top level while board === 'BIN': list BIN elements whose parentId is null
    //    OR whose parent is not on BIN (so nested binned items are accessible).
    //    Checklist files: list BIN items whose parentContainerId is rootId OR whose
    //    parent container is not a BIN element.
    let childElements: ExplorerEntry[]
    if (openElement) {
      childElements = elements
        .filter((element) => {
          if ((element.parentId ?? null) !== parentId) return false
          const isBin = (element.board ?? 'DASHBOARD') === 'BIN'
          return board === 'BIN' ? isBin : !isBin
        })
        .sort((first, second) => first.type.localeCompare(second.type) || first.title.localeCompare(second.title))
        .map((element): ExplorerEntry => ({ kind: 'element', id: element.id, element }))
    } else if (board === 'BIN') {
      childElements = boardElements
        .filter((element) => {
          if (!element.parentId) return true
          const parent = elementById.get(element.parentId)
          return (parent?.board ?? 'DASHBOARD') !== 'BIN'
        })
        .sort((first, second) => first.type.localeCompare(second.type) || first.title.localeCompare(second.title))
        .map((element): ExplorerEntry => ({ kind: 'element', id: element.id, element }))
    } else {
      childElements = boardElements
        .filter((element) => (element.parentId ?? null) === null)
        .sort((first, second) => first.type.localeCompare(second.type) || first.title.localeCompare(second.title))
        .map((element): ExplorerEntry => ({ kind: 'element', id: element.id, element }))
    }

    let files: ExplorerEntry[]
    if (openElement) {
      files = checklistItems
        .filter((item) => !item.isRecurrenceInstance)
        .filter((item) => item.parentContainerId === containerId)
        .filter((item) => !isChecklistGate(item))
        .filter((item) => {
          const isBin = (item.board ?? 'DASHBOARD') === 'BIN'
          return board === 'BIN' ? isBin : !isBin
        })
        .sort((first, second) => first.title.localeCompare(second.title))
        .map((item): ExplorerEntry => ({ kind: 'checklist', id: item.id, item }))
    } else if (board === 'BIN') {
      files = checklistItems
        .filter((item) => !item.isRecurrenceInstance)
        .filter((item) => !isChecklistGate(item))
        .filter((item) => (item.board ?? 'DASHBOARD') === 'BIN')
        .filter((item) => {
          if (item.parentContainerId === rootId) return true
          const parent = item.parentContainerId ? elementById.get(item.parentContainerId) : null
          return !parent || (parent.board ?? 'DASHBOARD') !== 'BIN'
        })
        .sort((first, second) => first.title.localeCompare(second.title))
        .map((item): ExplorerEntry => ({ kind: 'checklist', id: item.id, item }))
    } else {
      files = checklistItems
        .filter((item) => !item.isRecurrenceInstance)
        .filter((item) => item.parentContainerId === containerId)
        .filter((item) => !isChecklistGate(item))
        .filter((item) => (item.board ?? 'DASHBOARD') !== 'BIN')
        .sort((first, second) => first.title.localeCompare(second.title))
        .map((item): ExplorerEntry => ({ kind: 'checklist', id: item.id, item }))
    }
    return [...childElements, ...files]
  }, [board, boardElements, checklistItems, elementById, elements, openElement, rootId])

  const selectedElement = isElementEntry(selectedEntry)
    ? (elementById.get(selectedEntry.id) ?? selectedEntry.element)
    : null
  const selectedChecklist = isChecklistEntry(selectedEntry)
    ? (checklistItems.find((item) => item.id === selectedEntry.id) ?? selectedEntry.item)
    : null
  // Merge the buffered edits into the rendered shape so every input reflects
  // what the user has typed without committing yet.
  const effectiveElement = selectedElement
    ? { ...selectedElement, ...(pendingChanges as Partial<TimelineElement>) }
    : null
  const effectiveChecklist = selectedChecklist
    ? { ...selectedChecklist, ...(pendingChanges as Partial<ChecklistItem>) }
    : null

  const attachmentTarget: ItemAttachmentTarget | null = effectiveElement
    ? {
        id: effectiveElement.id,
        workspaceId: effectiveElement.workspaceId ?? activeWorkspaceId ?? '',
        title: effectiveElement.title,
        kind: effectiveElement.type,
        collectionName: 'elements',
        attachedFormIds: effectiveElement.attachedFormIds,
        attachedDesignIds: effectiveElement.attachedDesignIds,
        attachedUploadIds: effectiveElement.attachedUploadIds,
        attachedLinkIds: effectiveElement.attachedLinkIds,
        assigneeIds: effectiveElement.assignedEditors,
      }
    : effectiveChecklist
      ? {
          id: effectiveChecklist.id,
          workspaceId: effectiveChecklist.workspaceId ?? activeWorkspaceId ?? '',
          title: effectiveChecklist.title,
          kind: 'CHECKLIST',
          collectionName: 'checklists',
          attachedFormIds: effectiveChecklist.attachedFormIds,
          attachedDesignIds: effectiveChecklist.attachedDesignIds,
          attachedUploadIds: effectiveChecklist.attachedUploadIds,
          attachedLinkIds: effectiveChecklist.attachedLinkIds,
          assigneeIds: effectiveChecklist.assigneeId ? [effectiveChecklist.assigneeId] : [],
        }
      : null

  const setField = (key: string, value: unknown) => {
    setPendingChanges((current) => ({ ...current, [key]: value }))
    // First edit on a saved item: stamp it as 'modified' so the toolbar
    // surfaces Save/Discard, the leave guard fires, and discard can revert.
    if (!selectedEntry) return
    const currentState = manageDraft.getState(selectedEntry.id)
    if (currentState !== 'saved') return
    if (selectedEntry.kind === 'element') {
      manageDraft.markModified(
        selectedEntry.id,
        manageItemKindFor(selectedEntry.element.type),
        selectedEntry.element.parentId ?? null,
        selectedEntry.element,
      )
    } else {
      manageDraft.markModified(
        selectedEntry.id,
        'checklist',
        selectedEntry.item.parentContainerId ?? null,
        selectedEntry.item,
      )
    }
  }

  const collectDescendantIds = useCallback((elementId: string): string[] => {
    const ids: string[] = []
    const walk = (parentId: string) => {
      boardElements.filter((element) => element.parentId === parentId).forEach((child) => {
        ids.push(child.id)
        walk(child.id)
      })
    }
    walk(elementId)
    return ids
  }, [boardElements])

  type BoardLimitWarning = {
    message: string
    featureKey?: string
    limit?: number
    currentUsage?: number
  }

  const checkBoardLimits = useCallback((targetBoard: TimelineBoardKey, entry: ExplorerEntry, targetParentId: string | null): BoardLimitWarning | null => {
    const targetElements = elements.filter((element) => (element.board ?? 'DASHBOARD') === targetBoard)
    const targetChecklist = checklistItems.filter((item) => (item.board ?? 'DASHBOARD') === targetBoard && !isChecklistGate(item))
    const isNewEntry = entry.id === 'new'
    const movingElementIds = entry.kind === 'element' && !isNewEntry ? [entry.element.id, ...collectDescendantIds(entry.element.id)] : []
    const movingElements = entry.kind === 'element'
      ? isNewEntry ? [entry.element] : elements.filter((element) => movingElementIds.includes(element.id))
      : []

    const addedGoals = entry.kind === 'element'
      ? isNewEntry
        ? entry.element.type === 'GOAL' ? 1 : 0
        : movingElements.filter((element) => element.type === 'GOAL' && (element.board ?? 'DASHBOARD') !== targetBoard).length
      : 0
    const addedTasks = entry.kind === 'element'
      ? isNewEntry
        ? entry.element.type === 'TASK' ? 1 : 0
        : movingElements.filter((element) => element.type === 'TASK' && (element.board ?? 'DASHBOARD') !== targetBoard).length
      : 0
    const addedSubtasks = entry.kind === 'element'
      ? isNewEntry
        ? entry.element.type === 'SUBTASK' ? 1 : 0
        : movingElements.filter((element) => element.type === 'SUBTASK' && (element.board ?? 'DASHBOARD') !== targetBoard).length
      : 0
    const addedChecklists = entry.kind === 'checklist' && (isNewEntry || (entry.item.board ?? 'DASHBOARD') !== targetBoard) ? 1 : 0

    const counter = boardCounters[targetBoard]
    const existingGoalCount = counter ? counter.goals : targetElements.filter((element) => element.type === 'GOAL').length
    const existingTaskCount = counter ? counter.tasks : targetElements.filter((element) => element.type === 'TASK').length
    const existingSubtaskCount = counter ? counter.subtasks : targetElements.filter((element) => element.type === 'SUBTASK').length
    const existingChecklistCount = counter && typeof counter.checklists === 'number'
      ? counter.checklists
      : targetChecklist.length

    const limits = activeWorkspace?.workspaceLimits
    const maxGoals = limits ? getWorkspaceLimitValue(limits, 'maxGoalsPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxGoalsPerBoard
    const maxTasks = limits ? getWorkspaceLimitValue(limits, 'maxTasksPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxTasksPerBoard
    const maxSubtasks = limits ? getWorkspaceLimitValue(limits, 'maxSubtasksPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxSubtasksPerBoard
    const maxChecklists = limits ? getWorkspaceLimitValue(limits, 'maxChecklistsPerBoard', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxChecklistsPerBoard
    const maxTasksPerGoal = limits ? getWorkspaceLimitValue(limits, 'maxTasksPerGoal', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxTasksPerGoal
    const maxSubtasksPerTask = limits ? getWorkspaceLimitValue(limits, 'maxSubtasksPerTask', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxSubtasksPerTask
    const maxChecklistsPerContainer = limits ? getWorkspaceLimitValue(limits, 'maxChecklistsPerContainer', targetBoard) : WORKSPACE_COUNT_HARD_CAPS.maxChecklistsPerContainer

    if (addedGoals > 0 && existingGoalCount + addedGoals > maxGoals) {
      return {
        message: getNlsString('NLSBoardLimitGoalsReached', { limit: maxGoals }),
        featureKey: CAP_KEY_TO_FEATURE_KEY.maxGoalsPerBoard,
        limit: maxGoals,
        currentUsage: existingGoalCount,
      }
    }
    if (addedTasks > 0 && existingTaskCount + addedTasks > maxTasks) {
      return {
        message: getNlsString('NLSBoardLimitTasksReached', { limit: maxTasks }),
        featureKey: CAP_KEY_TO_FEATURE_KEY.maxTasksPerBoard,
        limit: maxTasks,
        currentUsage: existingTaskCount,
      }
    }
    if (addedSubtasks > 0 && existingSubtaskCount + addedSubtasks > maxSubtasks) {
      return {
        message: getNlsString('NLSBoardLimitSubtasksReached', { limit: maxSubtasks }),
        featureKey: CAP_KEY_TO_FEATURE_KEY.maxSubtasksPerBoard,
        limit: maxSubtasks,
        currentUsage: existingSubtaskCount,
      }
    }
    if (addedChecklists > 0 && existingChecklistCount + addedChecklists > maxChecklists) {
      return {
        message: getNlsString('NLSBoardLimitChecklistsReached', { limit: maxChecklists }),
        featureKey: CAP_KEY_TO_FEATURE_KEY.maxChecklistsPerBoard,
        limit: maxChecklists,
        currentUsage: existingChecklistCount,
      }
    }
    if (entry.kind === 'element' && entry.element.type === 'TASK' && targetParentId) {
      const pCounter = parentCounters[targetParentId]
      const currentTasks = pCounter ? pCounter.tasks : targetElements.filter((element) => element.type === 'TASK' && element.parentId === targetParentId).length
      if (currentTasks >= maxTasksPerGoal) {
        return {
          message: getNlsString('NLSParentLimitTasksReached', { limit: maxTasksPerGoal }),
          featureKey: CAP_KEY_TO_FEATURE_KEY.maxTasksPerGoal,
          limit: maxTasksPerGoal,
          currentUsage: currentTasks,
        }
      }
    }
    if (entry.kind === 'element' && entry.element.type === 'SUBTASK' && targetParentId) {
      const pCounter = parentCounters[targetParentId]
      const currentSubtasks = pCounter ? pCounter.subtasks : targetElements.filter((element) => element.type === 'SUBTASK' && element.parentId === targetParentId).length
      if (currentSubtasks >= maxSubtasksPerTask) {
        return {
          message: getNlsString('NLSParentLimitSubtasksReached', { limit: maxSubtasksPerTask }),
          featureKey: CAP_KEY_TO_FEATURE_KEY.maxSubtasksPerTask,
          limit: maxSubtasksPerTask,
          currentUsage: currentSubtasks,
        }
      }
    }
    if (entry.kind === 'checklist') {
      const pCounterId = targetParentId ?? rootId
      const pCounter = parentCounters[pCounterId]
      const currentChecklists = pCounter && typeof pCounter.checklists === 'number'
        ? pCounter.checklists
        : targetChecklist.filter((item) => item.parentContainerId === pCounterId).length
      const isSameContainer = !isNewEntry && entry.item.parentContainerId === pCounterId
      if (!isSameContainer && currentChecklists >= maxChecklistsPerContainer) {
        return {
          message: getNlsString('NLSParentLimitChecklistsReached', { limit: maxChecklistsPerContainer }),
          featureKey: CAP_KEY_TO_FEATURE_KEY.maxChecklistsPerContainer,
          limit: maxChecklistsPerContainer,
          currentUsage: currentChecklists,
        }
      }
    }
    return null
  }, [activeWorkspace?.workspaceLimits, boardCounters, checklistItems, collectDescendantIds, elements, parentCounters, rootId])

  const showLimitWarningToast = useCallback((warning: BoardLimitWarning) => {
    const action = warning.featureKey
      ? {
          label: 'Upgrade',
          run: () => {
            window.dispatchEvent(
              new CustomEvent('linkhub:paywall-required', {
                detail: {
                  featureKey: warning.featureKey,
                  limit: warning.limit,
                  currentUsage: warning.currentUsage,
                },
              }),
            )
          },
        }
      : undefined
    showToast(warning.message, 'warning', action)
  }, [showToast])

  const savePendingChanges = async (): Promise<boolean> => {
    if (!selectedEntry || !canEditBoard || !canPerformOnNode('board.edit', selectedEntry.id) || !hasPendingChanges) return false
    if (selectedEntry.kind === 'element') {
      const element = elements.find((e) => e.id === selectedEntry.id) ?? selectedEntry.element
      await updateElement(element.id, pendingChanges as Partial<TimelineElement>)
      const nextTitle = (pendingChanges as Partial<TimelineElement>).title ?? element.title
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: element.workspaceId,
        actorId: currentUser?.uid,
        actorName: currentDisplayName || currentUser?.email,
        targetType: element.type,
        targetId: element.id,
        targetTitle: nextTitle,
        action: 'UPDATE',
        summary: `Updated ${element.type.toLowerCase()} ${nextTitle}`,
        board,
      })
      setSelectedEntry({
        kind: 'element',
        id: element.id,
        element: { ...element, ...(pendingChanges as Partial<TimelineElement>) },
      })
    } else {
      const item = selectedEntry.item
      await saveBoardExplorerChecklistChanges({
        item,
        pendingChanges: pendingChanges as Record<string, unknown>,
        actor: {
          uid: currentUser?.uid ?? '',
          displayName: currentDisplayName || currentUser?.email || null,
        },
      })
      const nextTitle = (pendingChanges as Partial<ChecklistItem>).title ?? item.title
      await recordWorkspaceActivity(firestoreDb, {
        workspaceId: item.workspaceId,
        actorId: currentUser?.uid,
        actorName: currentDisplayName || currentUser?.email,
        targetType: 'CHECKLIST',
        targetId: item.id,
        targetTitle: nextTitle,
        action: 'CHECKLIST_UPDATED',
        summary: `Updated checklist file ${nextTitle}`,
        board,
      })
      setSelectedEntry({
        kind: 'checklist',
        id: item.id,
        item: { ...item, ...(pendingChanges as Partial<ChecklistItem>) },
      })
    }
    setPendingChanges({})
    if (selectedEntry) manageDraft.markSaved(selectedEntry.id)
    showToast('Changes saved.', 'success')
    return true
  }

  const [isSaving, setIsSaving] = useState(false)

  const handleMobileSave = useCallback(async () => {
    if (!hasPendingChanges || !canEditBoard) return
    setIsSaving(true)
    try {
      const ok = await savePendingChanges()
      if (ok) {
        setSelectedEntry(null)
      }
    } finally {
      setIsSaving(false)
    }
  }, [canEditBoard, hasPendingChanges, savePendingChanges, setSelectedEntry])

  const handleMobileDiscard = useCallback(() => {
    setPendingChanges({})
    setSelectedEntry(null)
  }, [setPendingChanges, setSelectedEntry])

  const handleMobileClose = useCallback(() => {
    requireConfirmIfPending(
      'You have unsaved changes. Discard them and switch?',
      () => {
        setPendingChanges({})
        setSelectedEntry(null)
      },
    )
  }, [requireConfirmIfPending, setPendingChanges, setSelectedEntry])

  // ---------------------------------------------------------------------------
  // Draft / modified state management (per new Manage spec)
  // ---------------------------------------------------------------------------

  // Map: every record in the registry → does it belong to this board? Used to
  // scope "Save children" / "Discard children" intersections.
  // Returns the set of draft/modified ids in the subtree rooted at the given
  // element id (inclusive). Used by Save-children / Discard-children buttons.
  const collectUnsavedSubtree = useCallback((rootId: string): string[] => {
    const result = new Set<string>()
    const considerSelf = manageDraft.getState(rootId) !== 'saved'
    if (considerSelf) result.add(rootId)
    const descendantIds = collectDescendantIds(rootId)
    descendantIds.forEach((id) => {
      if (manageDraft.getState(id) !== 'saved') result.add(id)
    })
    // Also include checklist files anchored under this element.
    checklistItems.forEach((item) => {
      if (item.parentContainerId === rootId && manageDraft.getState(item.id) !== 'saved') {
        result.add(item.id)
      }
    })
    return Array.from(result)
  }, [manageDraft, collectDescendantIds, checklistItems])

  // Save a single item. For drafts the item is already in Firestore (per the
  // pragmatic in-memory draft model) so saving just clears the registry. For
  // modified items it commits the buffered edits.
  const saveItem = useCallback(async (id: string) => {
    const record = manageDraft.getRecord(id)
    if (!record) return
    if (selectedEntry?.id === id && hasPendingChanges) {
      await savePendingChanges()
      return
    }
    manageDraft.markSaved(id)
  }, [hasPendingChanges, manageDraft, savePendingChanges, selectedEntry])

  // Save many ids in a parent-first order so a saved task can never reference a
  // draft goal (orphan rule).
  const saveMany = useCallback(async (ids: ReadonlyArray<string>) => {
    if (ids.length === 0) return
    let flushedSelected = false
    if (selectedEntry && ids.includes(selectedEntry.id) && hasPendingChanges) {
      await savePendingChanges()
      flushedSelected = true
    }
    // Topo-sort: goals → tasks → subtasks → checklist; ties resolved by id.
    const depthRank: Record<ManageItemKind, number> = { goal: 0, task: 1, subtask: 2, checklist: 3 }
    const sorted = [...ids].sort((a, b) => {
      const ra = manageDraft.getRecord(a)
      const rb = manageDraft.getRecord(b)
      const da = ra ? depthRank[ra.kind] : 99
      const db = rb ? depthRank[rb.kind] : 99
      return da - db
    })
    for (const id of sorted) {
      if (flushedSelected && id === selectedEntry?.id) continue
      await saveItem(id)
    }
  }, [hasPendingChanges, manageDraft, saveItem, savePendingChanges, selectedEntry])

  // Discard a single item. Drafts: delete Firestore doc(s) (and cascade for
  // elements). Modified: revert pendingChanges + clear from registry.
  const discardItem = useCallback(async (id: string) => {
    const record = manageDraft.getRecord(id)
    if (!record) return
    if (record.state === 'modified') {
      if (selectedEntry?.id === id) setPendingChanges({})
      manageDraft.clearRecord(id)
      return
    }
    // 'draft' — remove from Firestore. For elements, cascade descendants.
    if (record.kind === 'checklist') {
      try {
        await deleteChecklistNative(
          id,
          { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
        )
      } catch (error) {
        console.warn('[BoardExplorer] checklist discard failed', error)
      }
      manageDraft.clearRecord(id)
      if (selectedEntry?.id === id) setSelectedEntry(null)
      return
    }
    if (activeWorkspaceId) {
      try {
        const el = elements.find((e) => e.id === id)
        if (el && el.board !== 'BIN') {
          await binElements(activeWorkspaceId, [id])
        } else {
          await deleteForeverElements(activeWorkspaceId, [id])
        }
      } catch (error) {
        console.warn('[BoardExplorer] element discard failed', error)
      }
    }
    const cascadeIds = [id, ...collectDescendantIds(id)]
    manageDraft.clearMany(cascadeIds)
    if (selectedEntry && cascadeIds.includes(selectedEntry.id)) setSelectedEntry(null)
  }, [activeWorkspaceId, collectDescendantIds, currentDisplayName, currentUser?.email, currentUser?.uid, manageDraft, selectedEntry, setPendingChanges, setSelectedEntry])

  const discardMany = useCallback(async (ids: ReadonlyArray<string>) => {
    if (ids.length === 0) return
    // Parents last so deletions don't cascade-trip on a missing parent.
    const depthRank: Record<ManageItemKind, number> = { checklist: 0, subtask: 1, task: 2, goal: 3 }
    const sorted = [...ids].sort((a, b) => {
      const ra = manageDraft.getRecord(a)
      const rb = manageDraft.getRecord(b)
      const da = ra ? depthRank[ra.kind] : 99
      const db = rb ? depthRank[rb.kind] : 99
      return da - db
    })
    for (const id of sorted) {
      await discardItem(id)
    }
  }, [manageDraft, discardItem])

  const allUnsavedIds = useMemo(() => Array.from(manageDraft.records.keys()), [manageDraft.records])

  // Enable/disable selectors per spec.
  const selectedState = selectedEntry ? manageDraft.getState(selectedEntry.id) : 'saved'
  const selectedRecord = selectedEntry ? manageDraft.getRecord(selectedEntry.id) : undefined
  // Orphan rule: a draft can't be saved while its parent is also a draft.
  const selectedHasDraftParent = selectedRecord?.parentId
    ? manageDraft.getState(selectedRecord.parentId) === 'draft'
    : false
  const selectedExternalLock = selectedEntry ? getExternalLock(selectedEntry.id) : null
  const selectedIsExternallyLocked = !!selectedExternalLock
  const selectedSubtreeUnsaved = useMemo(
    () => (selectedEntry && selectedEntry.kind === 'element' ? collectUnsavedSubtree(selectedEntry.id) : []),
    [selectedEntry, collectUnsavedSubtree],
  )
  const selectedSubtreeUnsavedExcludingSelf = useMemo(
    () => selectedSubtreeUnsaved.filter((id) => id !== selectedEntry?.id),
    [selectedSubtreeUnsaved, selectedEntry],
  )

  const canSaveSelected =
    !!selectedEntry &&
    (selectedState === 'draft' || selectedState === 'modified') &&
    !selectedHasDraftParent &&
    !selectedIsExternallyLocked &&
    canPerformOnNode('board.edit', selectedEntry.id)
  const canSaveChildren = selectedSubtreeUnsavedExcludingSelf.length > 0 && !selectedIsExternallyLocked
  const canSaveAll = manageDraft.hasUnsavedDrafts
  const canDiscardSelected =
    !!selectedEntry &&
    (selectedState === 'draft' || selectedState === 'modified') &&
    !selectedIsExternallyLocked
  const canDiscardChildren = selectedSubtreeUnsavedExcludingSelf.length > 0 && !selectedIsExternallyLocked
  const canDiscardAll = manageDraft.hasUnsavedDrafts
  // Deletes go to the bin. Drafts shouldn't go to the bin — they should be
  // discarded instead — so the Delete button hides for pure drafts.
  const canDeleteSelected =
    !!selectedEntry &&
    selectedState !== 'draft' &&
    !selectedIsExternallyLocked &&
    canPerformOnNode('board.edit', selectedEntry.id)

  // Pending confirm payloads for the discard variants (single / children / all).
  type DiscardScope =
    | { kind: 'one'; id: string; label: string }
    | { kind: 'children'; ids: string[]; label: string }
    | { kind: 'all'; ids: string[] }
  const [pendingDiscardScope, setPendingDiscardScope] = useState<DiscardScope | null>(null)

  const runDiscard = useCallback(async () => {
    if (!pendingDiscardScope) return
    const scope = pendingDiscardScope
    setPendingDiscardScope(null)
    if (scope.kind === 'one') await discardItem(scope.id)
    else if (scope.kind === 'children') await discardMany(scope.ids)
    else await discardMany(allUnsavedIds)
  }, [pendingDiscardScope, discardItem, discardMany, allUnsavedIds])

  // Wire global Save-all / Discard-all handlers into ManageDraftContext so the
  // leave-with-drafts dialog (logout, beforeunload-confirm) can run them.
  useEffect(() => {
    manageDraft.setHandlers({
      saveAll: async () => {
        await saveMany(allUnsavedIds)
      },
      discardAll: async () => {
        await discardMany(allUnsavedIds)
      },
    })
  }, [manageDraft, saveMany, discardMany, allUnsavedIds])

  // Lease lifecycle: acquire a pessimistic edit lock for every element that
  // enters draft/modified state; release when the element is saved or
  // discarded. Diff-based so we don't spam Firestore on every render — one
  // acquire per new id, one release per removed id.
  useEffect(() => {
    const desired = new Set<string>(manageDraft.records.keys())
    const held = heldLeasesRef.current
    const toAcquire: string[] = []
    const toRelease: string[] = []
    desired.forEach((id) => { if (!held.has(id)) toAcquire.push(id) })
    held.forEach((id) => { if (!desired.has(id)) toRelease.push(id) })
    if (toAcquire.length === 0 && toRelease.length === 0) return
    heldLeasesRef.current = desired
    // Fire and forget — a lease failure just means another user is editing
    // (their conflict message surfaces on the next save attempt via the
    // read-side gate below).
    toAcquire.forEach((id) => {
      acquireEditLease(id).catch((error) => {
        console.warn('[BoardExplorer] acquireEditLease failed', id, error)
      })
    })
    toRelease.forEach((id) => {
      releaseLease(id).catch((error) => {
        console.warn('[BoardExplorer] releaseLease failed', id, error)
      })
    })
  }, [manageDraft.records, acquireEditLease, releaseLease])

  // Release every held lease on unmount so a Manage-close (or route away)
  // doesn't leave orphaned locks that keep teammates blocked.
  useEffect(() => {
    return () => {
      const held = heldLeasesRef.current
      if (held.size === 0) return
      held.forEach((id) => {
        releaseLease(id).catch((error) => {
          console.warn('[BoardExplorer] releaseLease on unmount failed', id, error)
        })
      })
      heldLeasesRef.current = new Set()
    }
    // Deliberately no deps — cleanup runs only on unmount, using the latest
    // releaseLease captured via ref-like closure (React re-creates the effect
    // when releaseLease changes, but the cleanup still sees the closed value).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const createElement = async (explicitType?: TimelineElementType) => {
    if (!activeWorkspaceId || !currentUser || !canEditBoard || !canPerformOnNode('board.edit', openElement?.id ?? null)) return
    if (openElement?.type === 'SUBTASK') {
      showToast('Subtasks cannot contain folders. Add checklist files here.', 'warning')
      return
    }
    const type = explicitType ?? nextElementType(openElement)
    const warning = checkBoardLimits(board, {
      kind: 'element',
      id: 'new',
      element: {
        ...createStripboardRecordDefaults(),
        id: 'new',
        type,
        title: '',
        weight: 1,
        progress: 0,
        parentId: openElement?.id ?? null,
        workspaceId: activeWorkspaceId,
        board,
        checklist: [],
        readAccess: [currentUser.uid],
        writeAccess: [currentUser.uid],
        directReadAccess: [currentUser.uid],
        directWriteAccess: [currentUser.uid],
      },
    }, openElement?.id ?? null)
    if (warning) {
      showLimitWarningToast(warning)
      return
    }
    const now = new Date().toISOString()
    const saved = await createElements([{
      ...createStripboardRecordDefaults({
        startDate: now,
        endDate: now,
      }),
      type,
      title: newElementTitle(type),
      description: '',
      weight: 1,
      progress: 0,
      parentId: openElement?.id ?? null,
      board,
    }])
    const created = saved[0]
    if (!created) return
    manageDraft.markDraft({
      id: created.id,
      kind: manageItemKindFor(type),
      parentId: openElement?.id ?? null,
    })
    showToast(`${newElementTitle(type)} created.`, 'success')
    setSelectedEntry({
      kind: 'element',
      id: created.id,
      element: created,
    })
  }

  const createChecklist = async () => {
    if (!activeWorkspaceId || !canEditBoard || !canPerformOnNode('board.edit', openElement?.id ?? null)) return
    const parentContainerId = openElement?.id ?? rootId
    const warning = checkBoardLimits(board, {
      kind: 'checklist',
      id: 'new',
      item: {
        ...createStripboardRecordDefaults({ boardStatus: 'TODO', priority: 3 }),
        id: 'new',
        title: '',
        workspaceId: activeWorkspaceId,
        parentContainerId,
        board,
        isCompleted: false,
        weightage: 1,
        position: { x: 0, y: 0 },
        prerequisites: [],
        blockedByExternal: null,
        isHelperVisible: true,
      },
    }, parentContainerId)
    if (warning) {
      showLimitWarningToast(warning)
      return
    }
    const newId = await createChecklistNative(
      {
        workspaceId: activeWorkspaceId,
        parentContainerId,
        title: 'New checklist file',
        priority: 3,
        weightage: 1,
        initialPosition: { x: 0, y: 0 },
        extra: {
          ...createStripboardRecordDefaults({ boardStatus: 'TODO', priority: 3 }),
          board,
          isHelperVisible: true,
          complexityStars: 3,
          urgencyStars: 3,
          importanceStars: 3,
          estimatedHours: null,
          currencyCode: 'USD',
          scheduleMode: 'DATED',
        },
      },
      { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
    )
    const docRef = { id: newId }
    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: activeWorkspaceId,
      actorId: currentUser?.uid,
      actorName: currentDisplayName || currentUser?.email,
      targetType: 'CHECKLIST',
      targetId: docRef.id,
      targetTitle: 'New checklist file',
      action: 'CHECKLIST_CREATED',
      summary: 'Created checklist file',
      board,
    })
    emitExperienceEvent('TRACK_CREATE_CHECK', {
      workspaceId: activeWorkspaceId,
      sourceId: docRef.id,
      increment: 1,
    })
    manageDraft.markDraft({
      id: docRef.id,
      kind: 'checklist',
      parentId: openElement?.id ?? null,
    })
    setSelectedEntry({
      kind: 'checklist',
      id: docRef.id,
      item: {
        id: docRef.id,
        title: 'New checklist file',
        parentContainerId,
        workspaceId: activeWorkspaceId,
        board,
        weightage: 1,
        initialPosition: { x: 0, y: 0 },
        isCompleted: false,
        prerequisites: [],
        blockedByExternal: null,
        isHelperVisible: true,
        complexityStars: 3,
        urgencyStars: 3,
        importanceStars: 3,
        estimatedHours: null,
        currencyCode: 'USD',
        scheduleMode: 'DATED',
        ...createStripboardRecordDefaults({ boardStatus: 'TODO', priority: 3 }),
      } as unknown as ChecklistItem,
    })
    showToast('Checklist file created.', 'success')
  }

  // Recurring books are created via the wizard inside ChecklistsShell now.
  const createRecurringChecklist = async () => {
    if (!activeWorkspaceId || !canEditBoard) return
    showToast('Recurring lives in Checklists → Recurring now. One book per parent, Daily or Weekly.', 'info')
  }

  const moveEntry = async (entry: ExplorerEntry, targetBoard: TimelineBoardKey, targetParentId: string | null) => {
    if (!canPerformOnNode('board.edit', entry.id)) return
    const warning = checkBoardLimits(targetBoard, entry, targetParentId)
    if (warning) {
      showLimitWarningToast(warning)
      return
    }
    if (entry.kind === 'checklist') {
      const nextParent = targetParentId ?? checklistRootId(entry.item.workspaceId, targetBoard)
      if (targetBoard === 'BIN') {
        await binChecklist(entry.item.workspaceId, [entry.id])
      } else if (entry.item.board === 'BIN') {
        await restoreChecklist(entry.item.workspaceId, [entry.id], targetBoard, nextParent)
      } else {
        await moveChecklist(entry.item.workspaceId, [entry.id], targetBoard, nextParent)
      }
      showToast(`Moved to ${TIMELINE_BOARD_LABELS[targetBoard]}.`, 'success')
      if (targetBoard === 'DASHBOARD') onDashboardMoveComplete?.(entry.id)
      return
    }
    const wsId = entry.element.workspaceId || activeWorkspaceId || ''
    if (targetBoard === 'BIN') {
      await binElements(wsId, [entry.element.id])
    } else if (entry.element.board === 'BIN') {
      await restoreElements(wsId, [entry.element.id], targetBoard, targetParentId)
    } else if (entry.element.board !== targetBoard) {
      await moveElements(wsId, [entry.element.id], targetBoard, targetParentId)
    } else {
      await reparentElements(wsId, [entry.element.id], targetParentId, targetBoard)
    }
    await recordWorkspaceActivity(firestoreDb, {
      workspaceId: wsId,
      actorId: currentUser?.uid,
      actorName: currentDisplayName || currentUser?.email,
      targetType: entry.element.type,
      targetId: entry.id,
      targetTitle: entry.element.title,
      action: targetBoard === 'BIN' ? 'DELETE' : targetBoard === entry.element.deletedFromBoard ? 'RESTORE' : 'MOVE_BOARD',
      summary: `Moved ${entry.element.title} to ${TIMELINE_BOARD_LABELS[targetBoard]}`,
      board: targetBoard,
    })
    showToast(`Moved to ${TIMELINE_BOARD_LABELS[targetBoard]}.`, 'success')
    if (targetBoard === 'DASHBOARD') onDashboardMoveComplete?.(entry.id)
  }

  const restoreEntry = async (entry: ExplorerEntry) => {
    const rawDeletedFrom = entry.kind === 'element'
      ? entry.element.deletedFromBoard
      : entry.item.deletedFromBoard
    const targetBoard = (rawDeletedFrom && rawDeletedFrom !== 'BIN') ? rawDeletedFrom : 'DASHBOARD'
    await moveEntry(entry, targetBoard, entry.kind === 'element' ? entry.element.parentId : entry.item.parentContainerId)
  }

  const deleteEntry = async (entry: ExplorerEntry) => {
    if (board === 'BIN') {
      // Confirmation is handled by the Delete button's ConfirmDialog gate, so
      // we skip the redundant window.confirm here.
      if (entry.kind === 'checklist') {
        await deleteChecklistNative(
          entry.id,
          { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
        )
        await recordWorkspaceActivity(firestoreDb, {
          workspaceId: entry.item.workspaceId || activeWorkspaceId || '',
          actorId: currentUser?.uid,
          actorName: currentDisplayName || currentUser?.email,
          targetType: 'CHECKLIST',
          targetId: entry.id,
          targetTitle: entry.item.title,
          action: 'CHECKLIST_DELETED',
          summary: `Deleted ${entry.item.title} forever`,
          board,
        })
      } else {
        const wsId = entry.element.workspaceId || activeWorkspaceId || ''
        await deleteForeverElements(wsId, [entry.element.id])
        await recordWorkspaceActivity(firestoreDb, {
          workspaceId: wsId,
          actorId: currentUser?.uid,
          actorName: currentDisplayName || currentUser?.email,
          targetType: entry.element.type,
          targetId: entry.id,
          targetTitle: entry.element.title,
          action: 'DELETE',
          summary: `Deleted ${entry.element.title} forever`,
          board,
        })
      }
      setSelectedEntry(null)
      showToast('Deleted forever.', 'success')
      return
    }
    await moveEntry(entry, 'BIN', entry.kind === 'element' ? entry.element.parentId : entry.item.parentContainerId)
  }

  boardOpsRef.current = {
    openCard: (id: string) => {
      setOpenElementId(id)
    },
    createCard: async () => {
      await createElement('TASK')
    },
    createColumn: async () => {
      await createElement('GOAL')
    },
    duplicateCard: async (id: string) => {
      const el = elements.find((e) => e.id === id)
      if (el && activeWorkspaceId && currentUser) {
        const now = new Date().toISOString()
        await createElements([{
          ...el,
          title: `${el.title} (Copy)`,
          board: el.board ?? 'DASHBOARD',
          parentId: el.parentId ?? null,
          startDate: el.startDate ?? now,
          endDate: el.endDate ?? now,
        }])
      }
    },
    deleteCard: async (id: string) => {
      const entry = children.find((e) => e.id === id)
      if (entry) {
        await deleteEntry(entry)
      } else {
        const el = elements.find((e) => e.id === id)
        if (el) {
          await deleteEntry({ kind: 'element', id: el.id, element: el })
        }
      }
    },
    moveCard: async (id: string) => {
      const entry = children.find((e) => e.id === id)
        ?? (elements.find((e) => e.id === id) ? { kind: 'element', id, element: elements.find((e) => e.id === id)! } as ExplorerEntry : null)
      if (entry) {
        setMoveIntent({ entry, targetBoard: 'DASHBOARD', sourceBoard: board })
      }
    },
  }

  const pasteIntoOpenFolder = async () => {
    if (!clipboard || !activeWorkspaceId || !canEditBoard) return
    const targetParentId = clipboard.entry.kind === 'checklist' ? openElement?.id ?? rootId : openElement?.id ?? null
    if (clipboard.entry.kind === 'element') {
      const movingType = clipboard.entry.element.type
      if (movingType === 'GOAL' && openElement) {
        showToast('Goals can only sit at the board root.', 'warning')
        return
      }
      if (movingType === 'TASK' && openElement && openElement.type !== 'GOAL') {
        showToast('Tasks can only sit at the board root or inside a goal.', 'warning')
        return
      }
      if (movingType === 'SUBTASK' && openElement && openElement.type !== 'TASK') {
        showToast('Subtasks can only sit at the board root or inside a task.', 'warning')
        return
      }
    }
    if (clipboard.op === 'cut') {
      const movingEntry = clipboard.entry
      await moveEntry(movingEntry, board, targetParentId)
      // Per the new draft model: cut+paste removes the original and re-adds
      // the item as a draft at the destination. The id is preserved so we
      // just flip it into draft state in the registry.
      const kind: ManageItemKind = movingEntry.kind === 'element'
        ? manageItemKindFor(movingEntry.element.type)
        : 'checklist'
      manageDraft.markDraft({
        id: movingEntry.id,
        kind,
        parentId: targetParentId,
      })
      setClipboard(null)
      showToast('Pasted as draft.', 'success')
      return
    }
    const warning = checkBoardLimits(board, clipboard.entry, targetParentId)
    if (warning) {
      showLimitWarningToast(warning)
      return
    }
    if (clipboard.entry.kind === 'checklist') {
      const source = clipboard.entry.item
      const newId = await createChecklistNative(
        {
          workspaceId: activeWorkspaceId,
          parentContainerId: targetParentId ?? source.parentContainerId,
          title: `${source.title} copy`,
          checklistType: (source.checklistType as 'INDIVIDUAL' | 'DAILY' | 'WEEKLY' | undefined) ?? 'INDIVIDUAL',
          priority: source.priority,
          weightage: source.weightage,
          initialPosition: source.position ?? { x: 0, y: 0 },
          extra: { board },
        },
        { uid: currentUser?.uid ?? '', displayName: currentDisplayName || currentUser?.email || null },
      )
      manageDraft.markDraft({
        id: newId,
        kind: 'checklist',
        parentId: targetParentId,
      })
      showToast('Pasted checklist file as draft.', 'success')
      return
    }
    const now = new Date().toISOString()
    const clone = cloneElementPayload(clipboard.entry.element, {
      workspaceId: activeWorkspaceId,
      board,
      parentId: targetParentId,
    })
    const saved = await createElements([{
      ...clone,
      startDate: clone.startDate ?? now,
      endDate: clone.endDate ?? now,
    }])
    const elementRef = saved[0]
    if (elementRef) {
      manageDraft.markDraft({
        id: elementRef.id,
        kind: manageItemKindFor(clipboard.entry.element.type),
        parentId: targetParentId,
      })
      showToast('Pasted folder as draft.', 'success')
    }
  }

  const resolveDragEntry = (event: DragEvent): ExplorerEntry | null => {
    try {
      const data = JSON.parse(event.dataTransfer.getData('application/x-timelining-entry')) as { kind: string; id: string }
      if (data.kind === 'element') {
        const element = boardElements.find((candidate) => candidate.id === data.id)
        return element ? { kind: 'element', id: element.id, element } : null
      }
      const item = checklistItems.find((candidate) => candidate.id === data.id)
      return item ? { kind: 'checklist', id: item.id, item } : null
    } catch {
      return null
    }
  }
  const handleDropOnElement = async (event: DragEvent, target: TimelineElement | null) => {
    event.preventDefault()
    if (!canEditBoard) return
    const entry = resolveDragEntry(event)
    if (!entry || !canPerformOnNode('board.edit', entry.id)) return
    if (entry.kind === 'element') {
      if (entry.id === target?.id) return
      if (entry.element.type === 'TASK' && target && target.type !== 'GOAL') {
        showToast('Tasks can only move to board root or into a goal.', 'warning')
        return
      }
      if (entry.element.type === 'SUBTASK' && target && target.type !== 'TASK') {
        showToast('Subtasks can only move to board root or into a task.', 'warning')
        return
      }
      if (entry.element.type === 'GOAL' && target) {
        showToast('Goals stay at board root.', 'warning')
        return
      }
    }
    await moveEntry(entry, board, entry.kind === 'checklist' ? target?.id ?? rootId : target?.id ?? null)
  }

  const selectableMoveParents = useMemo(() => {
    if (!moveIntent || moveIntent.targetBoard !== 'DASHBOARD') return []
    const dashboardElements = elements.filter((element) => (element.board ?? 'DASHBOARD') === 'DASHBOARD')
    if (moveIntent.entry.kind === 'element' && moveIntent.entry.element.type === 'TASK') {
      return dashboardElements.filter((element) => element.type === 'GOAL')
    }
    if (moveIntent.entry.kind === 'element' && moveIntent.entry.element.type === 'SUBTASK') {
      return dashboardElements.filter((element) => element.type === 'TASK')
    }
    if (moveIntent.entry.kind === 'checklist') return dashboardElements
    return []
  }, [elements, moveIntent])

  const renderTile = (entry: RenderableEntry) => {
    if (entry.kind === 'element' && pathOnlyElementIds.has(entry.id)) {
      return (
        <PathOnlyRow
          key={`${entry.kind}:${entry.id}`}
          title={entry.element.title}
          variant="tile"
        />
      )
    }
    const isFolder = entry.kind === 'element'
    const title = entry.kind === 'element' ? entry.element.title : entry.item.title
    const tileState = manageDraft.getState(entry.id)
    const stateClass =
      tileState === 'draft' ? ' isDraft'
      : tileState === 'modified' ? ' isModified'
      : ''
    const externalLock = getExternalLock(entry.id)
    const { rowLockedByPeer, lockTitle } = isRowLockedByPeer(externalLock, currentUser?.uid)
    return (
      <ExplorerTileItem
        key={`${entry.kind}:${entry.id}`}
        entry={entry}
        isFolder={isFolder}
        title={title}
        stateClass={stateClass}
        rowLockedByPeer={rowLockedByPeer}
        lockTitle={lockTitle}
        isSelected={selectedEntry?.id === entry.id}
        canEditBoard={canEditBoard && canPerformOnNode('board.edit', entry.id)}
        isMobile={isMobile}
        handleDropOnElement={handleDropOnElement}
        onOpenChecklistItem={onOpenChecklistItem}
        setOpenElementId={setOpenElementId}
        setSelectedEntry={setSelectedEntry}
        requireConfirmIfPending={requireConfirmIfPending}
      />
    )
  }

  useEffect(() => {
    const deleteLabel = board === 'BIN' ? 'Delete forever' : 'Move to bin'
    const clipboardItems: ButtonGroupItem[] = [
      {
        key: 'copy',
        label: 'Copy',
        icon: <ContentCopyRoundedIcon fontSize="inherit" />,
        disabled: !selectedEntry,
        onClick: () => selectedEntry && setClipboard({ op: 'copy', entry: selectedEntry }),
      },
      {
        key: 'cut',
        label: 'Cut',
        icon: <ContentCutRoundedIcon fontSize="inherit" />,
        disabled: !selectedEntry,
        onClick: () => selectedEntry && setClipboard({ op: 'cut', entry: selectedEntry }),
      },
      {
        key: 'paste',
        label: 'Paste as draft',
        icon: <ContentPasteRoundedIcon fontSize="inherit" />,
        disabled: !clipboard,
        onClick: () => void pasteIntoOpenFolder(),
      },
      {
        key: 'delete',
        label: deleteLabel,
        icon: <DeleteForeverRoundedIcon fontSize="inherit" />,
        tone: 'danger',
        disabled: !canDeleteSelected,
        onClick: () => selectedEntry && setPendingConfirm({
          message: board === 'BIN'
            ? 'Delete this item forever? This cannot be undone.'
            : `Move ${selectedEntry.kind === 'element' ? selectedEntry.element.title : selectedEntry.item.title} to the bin?`,
          action: () => void deleteEntry(selectedEntry),
        }),
      },
    ]

    const saveItems: ButtonGroupItem[] = [
      {
        key: 'save',
        label: 'Save',
        icon: <SaveRoundedIcon fontSize="inherit" />,
        tone: 'save',
        disabled: !canSaveSelected,
        title: selectedIsExternallyLocked
          ? `Locked — ${selectedExternalLock?.lockedByName ?? 'a teammate'} is editing`
          : selectedHasDraftParent
            ? 'Save the parent first — a draft cannot have a draft parent.'
            : 'Save',
        onClick: () => selectedEntry && void saveItem(selectedEntry.id),
      },
      {
        key: 'save-children',
        label: 'Save all children',
        icon: <SaveAsRoundedIcon fontSize="inherit" />,
        tone: 'save',
        disabled: !canSaveChildren,
        onClick: () => void saveMany(selectedSubtreeUnsavedExcludingSelf),
      },
      {
        key: 'save-all',
        label: 'Save all',
        icon: <SaveAltRoundedIcon fontSize="inherit" />,
        tone: 'save',
        disabled: !canSaveAll,
        title: 'Save all drafts (workspace-wide)',
        onClick: () => void saveMany(allUnsavedIds),
      },
    ]

    const discardItems: ButtonGroupItem[] = [
      {
        key: 'discard',
        label: 'Discard',
        icon: <RemoveCircleOutlineRoundedIcon fontSize="inherit" />,
        tone: 'danger',
        disabled: !canDiscardSelected,
        onClick: () => selectedEntry && setPendingDiscardScope({
          kind: 'one',
          id: selectedEntry.id,
          label: selectedEntry.kind === 'element'
            ? selectedEntry.element.title || 'this item'
            : selectedEntry.item.title || 'this item',
        }),
      },
      {
        key: 'discard-children',
        label: 'Discard all children',
        icon: <DeleteOutlineRoundedIcon fontSize="inherit" />,
        tone: 'danger',
        disabled: !canDiscardChildren,
        onClick: () => selectedEntry && setPendingDiscardScope({
          kind: 'children',
          ids: selectedSubtreeUnsavedExcludingSelf,
          label: selectedEntry.kind === 'element'
            ? selectedEntry.element.title || 'this folder'
            : selectedEntry.item.title || 'this folder',
        }),
      },
      {
        key: 'discard-all',
        label: 'Discard all',
        icon: <DeleteSweepRoundedIcon fontSize="inherit" />,
        tone: 'danger',
        disabled: !canDiscardAll,
        title: 'Discard all drafts (workspace-wide)',
        onClick: () => setPendingDiscardScope({ kind: 'all', ids: allUnsavedIds }),
      },
    ]

    // Only publish when Board mode is active. When Review is active, Board
    // stays dual-mounted but must not clobber Review's own publish.
    if (manageMode === 'review') {
      setManageActionsBar(null)
      return () => { /* no-op: Review owns the bar */ }
    }

    setManageActionsBar({
      canEditBoard: canEditBoard && canPerformOnNode('board.edit', openElement?.id ?? null),
      openElement,
      onCreateElement: (type) => void createElement(type),
      onCreateChecklist: () => void createChecklist(),
      onCreateRecurringChecklist: () => void createRecurringChecklist(),
      clipboardItems,
      saveItems,
      discardItems,
      headerControls,
    })

    return () => {
      setManageActionsBar(null)
    }
  }, [
    allUnsavedIds,
    board,
    canDeleteSelected,
    canDiscardAll,
    canDiscardChildren,
    canDiscardSelected,
    canEditBoard,
    canPerformOnNode,
    canSaveAll,
    canSaveChildren,
    canSaveSelected,
    clipboard,
    createElement,
    createChecklist,
    createRecurringChecklist,
    headerControls,
    manageMode,
    openElement,
    selectedEntry,
    selectedExternalLock,
    selectedHasDraftParent,
    selectedIsExternallyLocked,
    selectedSubtreeUnsavedExcludingSelf,
  ])

  return (
    <section data-orbit-target="canvas" className="boardExplorer">
      <div className={`boardExplorerBody${openElement || selectedEntry ? ' isSplit' : ''}${columnMode === '3col' && selectedEntry ? ' is3Col' : ''}`}>
        <main
          className="boardExplorerGridPane"
          data-tour-id="board-explorer-list"
          onDragOver={(event) => canEditBoard ? event.preventDefault() : undefined}
          onDrop={(event) => void handleDropOnElement(event, null)}
        >
          {openElement && (
            <ExplorerBackTileItem
              isMobile={isMobile}
              openElement={openElement}
              setOpenElementId={setOpenElementId}
              setSelectedEntry={setSelectedEntry}
              requireConfirmIfPending={requireConfirmIfPending}
            />
          )}
          {children.map(renderTile)}
          {children.length === 0 && <p className="boardExplorerEmpty">This folder is empty.</p>}
        </main>

        {!isMobile && columnMode === '3col' && selectedEntry && (
          <aside className="boardExplorerDetails boardExplorerDetails--old" aria-label="Saved version">
            <div className="boardExplorerDetailsHeader">
              <strong>{(() => {
                const snap = manageDraft.getRecord(selectedEntry.id)?.originalSnapshot as { title?: string } | undefined
                return snap?.title ?? (selectedEntry.kind === 'element' ? selectedEntry.element.title : selectedEntry.item.title) ?? 'Saved version'
              })()}</strong>
              <span className="boardExplorerCompareTag">Saved</span>
            </div>
            {(() => {
              const record = manageDraft.getRecord(selectedEntry.id)
              if (selectedState === 'draft') {
                return <p className="themeMuted" style={{ fontSize: 12 }}>This item is new — there's no saved version to compare yet.</p>
              }
              if (selectedState === 'saved') {
                return <p className="themeMuted" style={{ fontSize: 12 }}>No pending edits. Edit a field to see the diff appear here.</p>
              }
              const snap = record?.originalSnapshot as Record<string, unknown> | undefined
              if (!snap) {
                return <p className="themeMuted" style={{ fontSize: 12 }}>Saved snapshot unavailable.</p>
              }
              if (selectedEntry.kind === 'element') {
                const snapEl = snap as Partial<TimelineElement>
                return (
                  <div className="boardExplorerForm boardExplorerForm--readonly">
                    <label>Name<TextInput value={snapEl.title ?? ''} readOnly /></label>
                    <label>Description<Textarea value={snapEl.description ?? ''} readOnly /></label>
                    <label>Start<DatePicker value={normalizeDateForInput(snapEl.startDate)} readOnly onChange={() => undefined} ariaLabel="Start date" /></label>
                    <label>End<DatePicker value={normalizeDateForInput(snapEl.endDate)} readOnly onChange={() => undefined} ariaLabel="End date" /></label>
                  </div>
                )
              }
              const snapItem = snap as Partial<ChecklistItem>
              return (
                <div className="boardExplorerForm boardExplorerForm--readonly">
                  <label>Name<TextInput value={snapItem.title ?? ''} readOnly /></label>
                  <label>Weight<TextInput type="number" value={String(snapItem.weightage ?? 1)} readOnly /></label>
                  <label>Status<TextInput value={snapItem.workflowStatus ?? 'CREATED'} readOnly /></label>
                </div>
              )
            })()}
          </aside>
        )}

        {!isMobile && (openElement || selectedEntry) && (() => {
          const selectedNodeId = selectedEntry?.id ?? openElement?.id ?? null
          const canEditSelectedEntry = canEditBoard && Boolean(selectedNodeId && canPerformOnNode('board.edit', selectedNodeId))
          return (
            <aside className="boardExplorerDetails">
              <div className="boardExplorerDetailsHeader">
                <strong>{selectedElement?.title ?? selectedChecklist?.title ?? openElement?.title ?? TIMELINE_BOARD_LABELS[board]}</strong>
                <span>{selectedElement?.type ?? (selectedChecklist ? 'CHECKLIST' : 'FOLDER')}</span>
              </div>

              {effectiveElement && canEditSelectedEntry && (
                <div className="boardExplorerForm">
                  <label>Name<TextInput value={effectiveElement.title} disabled={!canEditBoard} onChange={(val) => setField('title', val)} /></label>
                  <label>Description<Textarea value={effectiveElement.description ?? ''} disabled={!canEditBoard} onChange={(val) => setField('description', val)} /></label>
                  <label>Start<DatePicker value={normalizeDateForInput(effectiveElement.startDate)} disabled={!canEditBoard} onChange={(raw) => setField('startDate', raw)} ariaLabel="Start date" /></label>
                  <label>End<DatePicker value={normalizeDateForInput(effectiveElement.endDate)} disabled={!canEditBoard} onChange={(raw) => setField('endDate', raw)} ariaLabel="End date" /></label>
                  {effectiveElement.type === 'GOAL' && (
                    <GoalClientPicker
                      workspaceId={effectiveElement.workspaceId ?? activeWorkspaceId ?? ''}
                      clientId={effectiveElement.clientId}
                      onChange={(clientId) => setField('clientId', clientId)}
                      disabled={!canEditBoard}
                    />
                  )}
                </div>
              )}

              {effectiveChecklist && canEditSelectedEntry && (
                <div className="boardExplorerChecklistColumns">
                  <div className="boardExplorerForm">
                    <label>Name<TextInput value={effectiveChecklist.title} disabled={!canEditBoard} onChange={(val) => setField('title', val)} /></label>
                    <label>Weight<TextInput type="number" min={1} value={String(effectiveChecklist.weightage ?? 1)} disabled={!canEditBoard} onChange={(val) => setField('weightage', Math.max(1, Math.round(Number(val) || 1)))} /></label>
                    <div className="boardExplorerField">
                      <span>Status</span>
                      <Select
                        trigger="pill"
                        ariaLabel="Workflow status"
                        value={effectiveChecklist.workflowStatus ?? 'CREATED'}
                        disabled={!canEditBoard}
                        options={['CREATED', 'ASSIGNED', 'ASSESSING', 'ACCEPTED', 'ONGOING', 'COMPLETED', 'NOT_REQUIRED', 'POSTPONED'].map((status) => ({ value: status, label: status }))}
                        onChange={(next) => setField('workflowStatus', next)}
                      />
                    </div>
                  </div>
                  <div className="boardExplorerForm">
                    <label>Complexity<TextInput type="number" min={1} max={5} value={String(effectiveChecklist.complexityStars ?? 3)} disabled={!canEditBoard} onChange={(val) => setField('complexityStars', Math.min(5, Math.max(1, Math.round(Number(val) || 3))))} /></label>
                    <label>Hours<TextInput type="number" min={0} value={String(effectiveChecklist.estimatedHours ?? 0)} disabled={!canEditBoard} onChange={(val) => setField('estimatedHours', Math.max(0, Math.round(Number(val) || 0)))} /></label>
                    <label>Cost<TextInput type="number" min={0} value={String(effectiveChecklist.actualCost ?? 0)} disabled={!canEditBoard} onChange={(val) => setField('actualCost', Math.max(0, Math.round(Number(val) || 0)))} /></label>
                  </div>
                </div>
              )}

              {attachmentTarget && (
                <div className="boardExplorerAttachmentsWrapper" style={{ marginTop: 12 }}>
                  <ItemAttachmentsPanel
                    target={attachmentTarget}
                    budgetLimit={effectiveElement?.budgetLimit ?? effectiveChecklist?.actualCost}
                    estimatedCost={effectiveElement?.estimatedCost}
                    canEdit={canEditSelectedEntry}
                    attachTourId="item-attach-provision"
                  />
                </div>
              )}

              {effectiveElement && (effectiveElement.type === 'GOAL' || effectiveElement.type === 'TASK') && (
                <div className="boardExplorerBudgetWrapper" style={{ marginTop: 12 }}>
                  <NodeBudgetLimitCard
                    element={effectiveElement}
                    workspace={activeWorkspace}
                    role={activeRole}
                    canEdit={canEditSelectedEntry}
                  />
                </div>
              )}

              {hasPendingChanges && (
                <div className="boardExplorerSaveRow boardExplorerSaveRow--hint" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                  <span className="themeMuted" style={{ fontSize: 12 }}>
                    Unsaved edits — use the Save / Discard buttons in the toolbar.
                  </span>
                </div>
              )}

              <div className="boardExplorerItemActions">
                {board === 'VISIONBOARD' && selectedEntry && canPerformOnNode('board.edit', selectedEntry.id) && (
                  <Button variant="secondary" size="sm" icon={<DriveFileMoveRoundedIcon fontSize="inherit" />} onClick={() => requireConfirmIfPending(
                    'You have unsaved changes. Discard them and move?',
                    () => setMoveIntent({ entry: selectedEntry, targetBoard: 'DASHBOARD' }),
                  )}>
                    Move to Dashboard
                  </Button>
                )}
                {board === 'ARCHIVE' && selectedEntry && canPerformOnNode('board.edit', selectedEntry.id) && (
                  <Button variant="secondary" size="sm" icon={<DriveFileMoveRoundedIcon fontSize="inherit" />} onClick={() => requireConfirmIfPending(
                    'You have unsaved changes. Discard them and move?',
                    () => setMoveIntent({ entry: selectedEntry, targetBoard: 'DASHBOARD' }),
                  )}>
                    Move to Dashboard
                  </Button>
                )}
                {board === 'BIN' && selectedEntry && canPerformOnNode('board.edit', selectedEntry.id) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    data-tour-id="board-explorer-restore"
                    icon={<RestoreFromTrashRoundedIcon fontSize="inherit" />}
                    onClick={() => requireConfirmIfPending(
                      'You have unsaved changes. Discard them and restore?',
                      () => void restoreEntry(selectedEntry),
                    )}
                  >
                    Restore
                  </Button>
                )}
              </div>
            </aside>
          )
        })()}

        {isMobile && (() => {
          const selectedNodeId = selectedEntry?.id ?? openElement?.id ?? null
          const canEditSelectedEntry = canEditBoard && Boolean(selectedNodeId && canPerformOnNode('board.edit', selectedNodeId))
          return (
            <MemberNodeDetailsSheet
              open={Boolean(selectedEntry)} onClose={handleMobileClose} element={effectiveElement} checklist={effectiveChecklist}
              canEdit={canEditSelectedEntry} isSaving={isSaving} hasPendingChanges={hasPendingChanges} onFieldChange={setField}
              onSave={() => void handleMobileSave()} onDiscard={handleMobileDiscard} attachmentTarget={attachmentTarget}
              actions={(board === 'VISIONBOARD' || board === 'ARCHIVE' || board === 'BIN') ? (
                <>
                  {board === 'VISIONBOARD' && selectedEntry && canPerformOnNode('board.edit', selectedEntry.id) && (
                    <Button variant="secondary" size="sm" icon={<DriveFileMoveRoundedIcon fontSize="inherit" />} onClick={() => requireConfirmIfPending(
                      'You have unsaved changes. Discard them and move?',
                      () => setMoveIntent({ entry: selectedEntry, targetBoard: 'DASHBOARD' }),
                    )}>
                      Move to Dashboard
                    </Button>
                  )}
                  {board === 'ARCHIVE' && selectedEntry && canPerformOnNode('board.edit', selectedEntry.id) && (
                    <Button variant="secondary" size="sm" icon={<DriveFileMoveRoundedIcon fontSize="inherit" />} onClick={() => requireConfirmIfPending(
                      'You have unsaved changes. Discard them and move?',
                      () => setMoveIntent({ entry: selectedEntry, targetBoard: 'DASHBOARD' }),
                    )}>
                      Move to Dashboard
                    </Button>
                  )}
                  {board === 'BIN' && selectedEntry && canPerformOnNode('board.edit', selectedEntry.id) && (
                    <Button variant="secondary" size="sm" icon={<RestoreFromTrashRoundedIcon fontSize="inherit" />} onClick={() => requireConfirmIfPending(
                      'You have unsaved changes. Discard them and restore?',
                      () => void restoreEntry(selectedEntry),
                    )}>
                      Restore
                    </Button>
                  )}
                </>
              ) : undefined}
            />
          )
        })()}
      </div>

      {moveIntent && (
        <AppDialog
          open={Boolean(moveIntent)}
          title="Choose destination"
          headerExtra={<p className="dropdownLabel">Move to {TIMELINE_BOARD_LABELS[moveIntent.targetBoard]}</p>}
          onDismiss={() => setMoveIntent(null)}
          actions={[
            {
              label: 'Cancel',
              variant: 'ghost',
              onClick: () => setMoveIntent(null),
            },
          ]}
        >
          {selectableMoveParents.length === 0 && (
            moveIntent.entry.kind === 'element' &&
            (moveIntent.entry.element.type === 'TASK' || moveIntent.entry.element.type === 'SUBTASK')
          ) ? (
            <p className="themeMuted">
              No valid Dashboard parent is available for this {moveIntent.entry.element.type.toLowerCase()}.
            </p>
          ) : selectableMoveParents.length === 0 ? (
            <Button variant="primary" onClick={() => {
              void moveEntry(moveIntent.entry, moveIntent.targetBoard, null)
              setMoveIntent(null)
            }}>
              Move to board root
            </Button>
          ) : (
            <div className="boardMoveOptions">
              {selectableMoveParents.map((parent) => (
                <Pressable key={parent.id} onClick={() => {
                  void moveEntry(moveIntent.entry, moveIntent.targetBoard, parent.id)
                  setMoveIntent(null)
                }}>
                  {elementIcon(parent.type)}
                  {parent.title}
                </Pressable>
              ))}
            </div>
          )}
        </AppDialog>
      )}
      {pendingConfirm && (
        <ConfirmDialog
          title="Are you sure?"
          body={pendingConfirm.message}
          confirmLabel="Yes, continue"
          cancelLabel="Cancel"
          tone="danger"
          onConfirm={() => {
            const action = pendingConfirm.action
            setPendingChanges({})
            setPendingConfirm(null)
            action()
          }}
          onCancel={() => setPendingConfirm(null)}
        />
      )}
      {pendingDiscardScope && (
        <ConfirmDialog
          title={
            pendingDiscardScope.kind === 'all'
              ? `Discard all ${pendingDiscardScope.ids.length} drafts?`
              : pendingDiscardScope.kind === 'children'
                ? `Discard ${pendingDiscardScope.ids.length} child draft${pendingDiscardScope.ids.length === 1 ? '' : 's'} under ${pendingDiscardScope.label}?`
                : `Discard "${pendingDiscardScope.label}"?`
          }
          body={
            pendingDiscardScope.kind === 'all'
              ? 'Every draft will be deleted and every modified item will revert to its last-saved version. This cannot be undone.'
              : pendingDiscardScope.kind === 'children'
                ? 'Drafts under this item will be deleted and modified items will revert to their last-saved version. This cannot be undone.'
                : 'Drafts are deleted forever. Modified items revert to their last-saved version.'
          }
          confirmLabel="Discard"
          cancelLabel="Cancel"
          tone="danger"
          onConfirm={runDiscard}
          onCancel={() => setPendingDiscardScope(null)}
        />
      )}
    </section>
  )
}
